import React, { useState } from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useAuth } from "../../context/AuthContext";
import {
  validatePasswordStrength,
  validatePasswordMatch,
  validatePasswordDifferent,
} from "../../lib/passwordUtils";

export const SettingsPasswordSection: React.FC = () => {
  const { updateUserPassword } = useAuth();

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {}
  );

  // Enhanced password validation using utility function
  const validatePassword = (password: string) => {
    return validatePasswordStrength(password);
  };

  // Get password strength
  const getPasswordStrength = () => {
    const errors = validatePassword(passwordData.newPassword);
    if (passwordData.newPassword.length === 0) return { strength: 0, text: "" };
    if (errors.length === 0) return { strength: 100, text: "Kuat" };
    if (errors.length <= 2) return { strength: 60, text: "Sedang" };
    return { strength: 30, text: "Lemah" };
  };

  // Handle password form submission
  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple simultaneous submissions
    if (isChangingPassword) {
      return;
    }

    // Validate password form
    const newErrors: Record<string, string> = {};

    if (!passwordData.currentPassword.trim()) {
      newErrors.currentPassword = "Password lama harus diisi";
    }

    const passwordValidationErrors = validatePassword(passwordData.newPassword);
    if (passwordValidationErrors.length > 0) {
      newErrors.newPassword = `Password harus mengandung ${passwordValidationErrors.join(", ")}`;
    }

    if (
      !validatePasswordMatch(
        passwordData.newPassword,
        passwordData.confirmNewPassword
      )
    ) {
      newErrors.confirmNewPassword = "Konfirmasi password tidak cocok";
    }

    if (
      !validatePasswordDifferent(
        passwordData.currentPassword,
        passwordData.newPassword
      )
    ) {
      newErrors.newPassword = "Password baru harus berbeda dari password lama";
    }

    setPasswordErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Clear any previous errors
    setPasswordErrors({});

    try {
      setIsChangingPassword(true);

      const result = await updateUserPassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (result.success) {
        // Clear password form on success
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
        // Clear any previous errors
        setPasswordErrors({});
      } else if (result.error) {
        setPasswordErrors({ submit: result.error });
      }
    } catch (error) {
      console.error("💥 Exception in handleSubmitPassword:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Terjadi kesalahan tidak terduga";
      setPasswordErrors({ submit: errorMessage });
    } finally {
      // Remove setTimeout workaround - direct state update is safer
      setIsChangingPassword(false);
    }
  };

  // Handle password input changes
  const handlePasswordInputChange = (
    field: keyof typeof passwordData,
    value: string
  ) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const passwordStrength = getPasswordStrength();

  // Check if password form is valid
  const isPasswordFormValid =
    passwordData.currentPassword.trim() !== "" &&
    passwordData.newPassword.trim() !== "" &&
    passwordData.confirmNewPassword.trim() !== "";

  return (
    <>
      {/* Change Password Section */}
      <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
        <h3 className="text-lg sm:text-xl font-semibold text-[#373e46] mb-4">
          Ubah Password
        </h3>
        <form onSubmit={handleSubmitPassword} className="space-y-4">
          {/* Current Password Field */}
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password Lama
            </label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={passwordData.currentPassword}
                onChange={e =>
                  handlePasswordInputChange("currentPassword", e.target.value)
                }
                className={`h-10 sm:h-12 rounded-xl border-2 pr-12 ${
                  passwordErrors.currentPassword
                    ? "border-red-500"
                    : "border-[#e3e6eb]"
                } focus:border-[#6b2cfe] transition-colors`}
                placeholder="Masukkan password lama"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showCurrentPassword ? (
                  <EyeOffIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-500">
                {passwordErrors.currentPassword}
              </p>
            )}
          </div>

          {/* New Password Field */}
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password Baru
            </label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={passwordData.newPassword}
                onChange={e =>
                  handlePasswordInputChange("newPassword", e.target.value)
                }
                className={`h-10 sm:h-12 rounded-xl border-2 pr-12 ${
                  passwordErrors.newPassword
                    ? "border-red-500"
                    : "border-[#e3e6eb]"
                } focus:border-[#6b2cfe] transition-colors`}
                placeholder="Masukkan password baru"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showNewPassword ? (
                  <EyeOffIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {passwordData.newPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        passwordStrength.strength >= 60
                          ? "bg-green-500"
                          : passwordStrength.strength >= 30
                            ? "bg-yellow-500"
                            : "bg-red-500"
                      }`}
                      style={{ width: `${passwordStrength.strength}%` }}
                    ></div>
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      passwordStrength.strength >= 60
                        ? "text-green-600"
                        : passwordStrength.strength >= 30
                          ? "text-yellow-600"
                          : "text-red-600"
                    }`}
                  >
                    {passwordStrength.text}
                  </span>
                </div>
              </div>
            )}

            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-500">
                {passwordErrors.newPassword}
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label
              htmlFor="confirmNewPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Konfirmasi Password Baru
            </label>
            <div className="relative">
              <Input
                id="confirmNewPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={passwordData.confirmNewPassword}
                onChange={e =>
                  handlePasswordInputChange(
                    "confirmNewPassword",
                    e.target.value
                  )
                }
                className={`h-10 sm:h-12 rounded-xl border-2 pr-12 ${
                  passwordErrors.confirmNewPassword
                    ? "border-red-500"
                    : "border-[#e3e6eb]"
                } focus:border-[#6b2cfe] transition-colors`}
                placeholder="Konfirmasi password baru"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? (
                  <EyeOffIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {passwordErrors.confirmNewPassword && (
              <p className="mt-1 text-sm text-red-500">
                {passwordErrors.confirmNewPassword}
              </p>
            )}
          </div>

          {/* Submit Error */}
          {passwordErrors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{passwordErrors.submit}</p>
            </div>
          )}

          {/* Error Display */}
          {passwordErrors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Gagal mengubah password
                  </h3>
                  <div className="mt-1 text-sm text-red-700">
                    {passwordErrors.submit}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isChangingPassword || !isPasswordFormValid}
            className={`w-full h-10 sm:h-12 font-semibold rounded-xl transition-all duration-300 ${
              isChangingPassword
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 hover:shadow-lg"
            } text-white disabled:opacity-50`}
          >
            {isChangingPassword ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                <span>Mengubah password...</span>
              </div>
            ) : (
              "Ubah Password"
            )}
          </Button>

          {/* Help Text */}
          <div className="text-xs text-gray-500 text-center">
            Password akan diubah untuk akun yang sedang aktif. Pastikan Anda
            mengingat password baru.
          </div>
        </form>
      </div>

      {/* Additional Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-blue-800 mb-2">
          Tips Keamanan
        </h3>
        <ul className="text-blue-700 text-sm space-y-1">
          <li>
            • Gunakan password yang kuat dengan kombinasi huruf besar, kecil,
            angka
          </li>
          <li>• Jangan gunakan password yang sama dengan akun lain</li>
          <li>• Ubah password secara berkala untuk keamanan yang lebih baik</li>
          <li>• Jangan bagikan informasi akun Anda kepada orang lain</li>
          <li>• Gunakan foto profil yang sesuai dan profesional</li>
        </ul>
      </div>
    </>
  );
};
